import { getStaffIdToken, signOutOfStaff } from "@/services/staff.services";
import { isAxiosError } from "axios";

/*
  Staff session
  =============

  The clicknpark API gets the staff ID token through getStaffSessionToken, which sdk-call-center calls
  before each request. Firebase renews the token there when it is about to expire, so nothing here
  keeps a timer to renew it.

  The staff member signs in again 7 days after signing in: the clicknpark API refuses an older
  sign-in with 401 `staff/session-expired`, so the next request ends the session first and shows the
  sign-in page.
*/

/** How long after signing in a staff member has to sign in again. */
const MAX_SESSION_AGE_MS = 7 * 24 * 60 * 60_000;

/** A request found the sign-in older than {@link MAX_SESSION_AGE_MS}; the session is being ended. */
export class StaffSessionExpiredError extends Error {
  public constructor() {
    super("The staff session reached its maximum age.");
    this.name = "StaffSessionExpiredError";
  }
}

/** Hears a sign-out that failed when the session reached its maximum age: the page stays where it is. */
let handleSignOutFailure: ((error: unknown) => void) | undefined;

/** Whether the session is being ended, so that requests that find it too old end it only once. */
let isExpiring = false;

/**
 * The staff ID token for a request to the clicknpark API, or undefined when nobody is signed in. A
 * sign-in older than {@link MAX_SESSION_AGE_MS} ends the session instead, and the request fails.
 */
export async function getStaffSessionToken(): Promise<string | undefined> {
  const idToken = await getStaffIdToken();
  if (!idToken) return undefined;

  if (Date.now() >= idToken.signedInAt.getTime() + MAX_SESSION_AGE_MS) {
    void expireStaffSession();
    throw new StaffSessionExpiredError();
  }

  return idToken.token;
}

/**
 * Hears a sign-out that failed when the session reached its maximum age, in place of the one before.
 * @returns Stops listening
 */
export function onStaffSignOutFailed(handler: (error: unknown) => void): () => void {
  handleSignOutFailure = handler;

  return (): void => {
    if (handleSignOutFailure === handler) handleSignOutFailure = undefined;
  };
}

/**
 * The session reached its maximum age: signs out in this browser and reloads, which shows the sign-in
 * page now that nobody is signed in.
 */
export async function expireStaffSession(): Promise<void> {
  if (isExpiring) return;
  isExpiring = true;

  try {
    await signOutOfStaff();
  } catch (error) {
    // Reloading now would restore the saved session and end it again, in a loop.
    console.error("Could not sign out of a staff session that reached its maximum age.", error);
    handleSignOutFailure?.(error);
    return;
  }

  window.location.href = "/";
}

/**
 * Whether a staff token was refused because its sign-in is past the maximum age: by the clicknpark API,
 * or here before the request went out.
 */
export function isStaffSessionExpiredError(error: unknown): boolean {
  if (error instanceof StaffSessionExpiredError) return true;
  if (!isAxiosError(error) || error.response?.status !== 401) return false;

  const body = error.response.data as { error?: { code?: unknown } } | undefined;
  return body?.error?.code === "staff/session-expired";
}
